<template>
  <teleport to="body">
    <div v-if="isActive && targetRect" class="tour-spotlight-wrapper" :style="wrapperStyle">
      
      <!-- Semi-transparent backdrop highlight -->
      <div class="tour-backdrop" @click="closeTour"></div>

      <!-- Pointer & Guide Card positioned near the target element -->
      <div class="tour-floating-box" :style="boxPositionStyle">
        
        <!-- When card is BELOW the target, arrow is on TOP pointing UP -->
        <div v-if="placement === 'bottom'" class="tour-arrow-container arrow-up" :style="arrowAlignmentStyle">
          <div class="tour-arrow-bounce">
            <i class="fa-solid fa-arrow-up fs-2 text-primary"></i>
            <span class="tour-arrow-pill">AICI</span>
          </div>
        </div>

        <!-- Sleek Interactive Tour Card -->
        <div class="tour-card shadow-lg rounded-4 p-4">
          <div class="d-flex align-items-center justify-content-between mb-2 pb-1 border-bottom border-light">
            <div class="d-flex align-items-center gap-2">
              <span class="tour-pulse-dot"></span>
              <span class="badge bg-primary text-white rounded-pill px-2.5 py-1 fs-9 fw-bold">
                <i class="fa-solid fa-compass me-1"></i> Ghid Interactiv
              </span>
              <h6 class="mb-0 fw-bolder text-gray-900 fs-7">{{ currentTour.title }}</h6>
            </div>
            <button type="button" class="btn btn-sm btn-icon btn-light rounded-circle hover-scale" @click="closeTour" title="Închide ghidul">
              <i class="fa-solid fa-xmark fs-7"></i>
            </button>
          </div>

          <div class="tour-body py-1">
            <p class="text-gray-700 fs-7 mb-0 lh-base" v-html="currentTour.message"></p>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top border-gray-100">
            <span class="text-muted fs-9 d-flex align-items-center gap-1">
              <i class="fa-solid fa-hand-pointer text-primary"></i>
              <span>Apasă butonul evidențiat sau</span>
            </span>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-xs btn-primary fw-bold rounded-pill px-3 py-1.5 shadow-xs" @click="closeTour">
                Am înțeles <i class="fa-solid fa-check ms-1 fs-9"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- When card is ABOVE the target, arrow is at the BOTTOM pointing DOWN -->
        <div v-if="placement === 'top'" class="tour-arrow-container arrow-down" :style="arrowAlignmentStyle">
          <div class="tour-arrow-bounce">
            <i class="fa-solid fa-arrow-down fs-2 text-primary"></i>
            <span class="tour-arrow-pill">AICI</span>
          </div>
        </div>

      </div>

    </div>
  </teleport>
</template>

<script>
export default {
  name: 'InteractiveTourSpotlight',
  data() {
    return {
      isActive: false,
      currentTour: {
        target: '',
        title: '',
        message: '',
        position: 'auto'
      },
      targetRect: null,
      placement: 'bottom',
      targetEl: null,
      pollInterval: null,
      pollCount: 0
    };
  },
  computed: {
    wrapperStyle() {
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1080,
        pointerEvents: 'none'
      };
    },
    boxPositionStyle() {
      if (!this.targetRect) return {};

      const rect = this.targetRect;
      const margin = 12;
      const boxWidth = Math.min(390, window.innerWidth - 32);

      // Center card relative to target element
      const targetCenterX = rect.left + rect.width / 2;
      let left = targetCenterX - boxWidth / 2;

      // Clamp horizontally inside viewport
      if (left < 16) left = 16;
      if (left + boxWidth > window.innerWidth - 16) {
        left = window.innerWidth - boxWidth - 16;
      }

      // Vertical placement (fixed viewport coordinates)
      let top = 0;
      if (this.placement === 'top') {
        top = Math.max(16, rect.top - margin);
      } else {
        top = Math.min(window.innerHeight - 250, rect.bottom + margin);
      }

      return {
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        width: `${boxWidth}px`,
        pointerEvents: 'auto',
        transform: this.placement === 'top' ? 'translateY(-100%)' : 'none'
      };
    },
    arrowAlignmentStyle() {
      if (!this.targetRect) return {};
      const rect = this.targetRect;
      const boxWidth = Math.min(390, window.innerWidth - 32);

      const targetCenterX = rect.left + rect.width / 2;
      let left = targetCenterX - boxWidth / 2;
      if (left < 16) left = 16;
      if (left + boxWidth > window.innerWidth - 16) {
        left = window.innerWidth - boxWidth - 16;
      }

      // Compute where arrow should sit along the card's width
      let arrowOffset = targetCenterX - left;
      if (arrowOffset < 36) arrowOffset = 36;
      if (arrowOffset > boxWidth - 36) arrowOffset = boxWidth - 36;

      return {
        display: 'flex',
        justifyContent: 'flex-start',
        paddingLeft: `${arrowOffset - 14}px`
      };
    }
  },
  mounted() {
    window.startInteractiveTour = (config) => {
      this.startTour(config);
    };

    if (window.emitter) {
      window.emitter.on('start-interactive-tour', this.startTour);
    }

    window.addEventListener('resize', this.updateRect);
    window.addEventListener('scroll', this.updateRect, true);
    window.addEventListener('keydown', this.handleKeyDown);

    // Check on initial load or navigation
    this.checkPendingTour();
  },
  beforeUnmount() {
    if (window.emitter) {
      window.emitter.off('start-interactive-tour', this.startTour);
    }
    window.removeEventListener('resize', this.updateRect);
    window.removeEventListener('scroll', this.updateRect, true);
    window.removeEventListener('keydown', this.handleKeyDown);
    this.cleanupHighlight();
  },
  watch: {
    $route() {
      this.$nextTick(() => {
        this.checkPendingTour();
      });
    }
  },
  methods: {
    checkPendingTour() {
      try {
        const stored = sessionStorage.getItem('pending_guide_tour');
        if (stored) {
          const config = JSON.parse(stored);
          sessionStorage.removeItem('pending_guide_tour');
          setTimeout(() => {
            this.startTour(config);
          }, 350);
        }
      } catch (e) {
        console.warn('Tour check error:', e);
      }
    },
    resolveTargetElement(selectorString) {
      if (!selectorString) return null;
      const selectors = selectorString.split(',').map(s => s.trim()).filter(Boolean);

      for (const sel of selectors) {
        try {
          const candidates = Array.from(document.querySelectorAll(sel));
          for (const el of candidates) {
            // Must have real dimensions
            const rect = el.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) continue;

            // Must be visible
            const style = window.getComputedStyle(el);
            if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') continue;

            // Reject elements inside navigation sidebars or header unless explicitly targeted
            const wantsNav = sel.includes('sidebar') || sel.includes('aside') || sel.includes('header') || sel.includes('navbar');
            if (!wantsNav) {
              const inNav = el.closest('#kt_app_sidebar, .app-sidebar, .sidebar, aside, .left-sidebar, .menu-sidebar, #kt_app_header, .app-header, .navbar');
              if (inNav) continue;
            }

            return el;
          }
        } catch (e) {
          console.warn('Tour selector warning:', sel, e);
        }
      }
      return null;
    },
    startTour(config) {
      if (!config || !config.target) return;

      this.currentTour = {
        target: config.target,
        title: config.title || 'Ghid Interactiv',
        message: config.message || 'Urmărește pasul indicat pe ecran.',
        position: config.position || 'auto'
      };

      this.pollCount = 0;
      clearInterval(this.pollInterval);

      // Poll until target element is rendered in DOM
      this.pollInterval = setInterval(() => {
        this.pollCount++;
        const el = this.resolveTargetElement(this.currentTour.target);

        if (el) {
          clearInterval(this.pollInterval);
          this.attachToElement(el);
        } else if (this.pollCount > 35) {
          // Timeout after 7 seconds
          clearInterval(this.pollInterval);
        }
      }, 200);
    },
    attachToElement(el) {
      this.cleanupHighlight();
      this.targetEl = el;

      // Add high-contrast glowing animation directly to target
      el.classList.add('tour-active-target');

      // Scroll into view smoothly
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

      // Dismiss if user clicks directly on the target
      el.addEventListener('click', this.onTargetClicked);

      setTimeout(() => {
        this.updateRect();
        this.isActive = true;
      }, 350);
    },
    updateRect() {
      if (!this.targetEl) return;
      this.targetRect = this.targetEl.getBoundingClientRect();

      const spaceAbove = this.targetRect.top;
      const spaceBelow = window.innerHeight - this.targetRect.bottom;
      const neededSpace = 240;

      if (this.currentTour.position === 'top') {
        this.placement = spaceAbove >= neededSpace ? 'top' : (spaceBelow >= neededSpace ? 'bottom' : 'top');
      } else if (this.currentTour.position === 'bottom') {
        this.placement = spaceBelow >= neededSpace ? 'bottom' : (spaceAbove >= neededSpace ? 'top' : 'bottom');
      } else {
        // Auto: prioritize whichever side has ample room, prefer bottom
        this.placement = spaceBelow >= neededSpace ? 'bottom' : (spaceAbove >= neededSpace ? 'top' : 'bottom');
      }
    },
    onTargetClicked() {
      // User performed the requested action!
      this.closeTour();
    },
    closeTour() {
      this.isActive = false;
      this.cleanupHighlight();
      clearInterval(this.pollInterval);
    },
    cleanupHighlight() {
      if (this.targetEl) {
        this.targetEl.classList.remove('tour-active-target');
        this.targetEl.removeEventListener('click', this.onTargetClicked);
        this.targetEl = null;
      }
    },
    handleKeyDown(e) {
      if (e.key === 'Escape' && this.isActive) {
        this.closeTour();
      }
    }
  }
};
</script>

<style>
/* Global high-visibility glowing halo on active target */
.tour-active-target {
  outline: 3px solid #009ef7 !important;
  outline-offset: 3px !important;
  box-shadow: 0 0 0 6px rgba(0, 158, 247, 0.35), 0 0 25px rgba(0, 158, 247, 0.5) !important;
  animation: tourGlowPulse 1.6s infinite ease-in-out !important;
  z-index: 1065 !important;
  transition: all 0.3s ease !important;
}

@keyframes tourGlowPulse {
  0% {
    box-shadow: 0 0 0 4px rgba(0, 158, 247, 0.5), 0 0 15px rgba(0, 158, 247, 0.4);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(0, 158, 247, 0.15), 0 0 30px rgba(0, 158, 247, 0.7);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 0 4px rgba(0, 158, 247, 0.5), 0 0 15px rgba(0, 158, 247, 0.4);
    transform: scale(1);
  }
}
</style>

<style scoped>
.tour-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.25);
  pointer-events: auto;
  backdrop-filter: blur(1.5px);
  animation: tourFadeIn 0.3s ease;
}

@keyframes tourFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.tour-floating-box {
  z-index: 1075;
  animation: tourSlidePop 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes tourSlidePop {
  from {
    opacity: 0;
    transform: translateY(15px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.tour-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 40px -8px rgba(0, 0, 0, 0.2), 0 0 20px rgba(0, 158, 247, 0.15) !important;
}

/* Pulsing blue status dot */
.tour-pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #009ef7;
  display: inline-block;
  box-shadow: 0 0 0 0 rgba(0, 158, 247, 0.7);
  animation: tourDotPulse 1.4s infinite;
}

@keyframes tourDotPulse {
  0% { box-shadow: 0 0 0 0 rgba(0, 158, 247, 0.7); }
  70% { box-shadow: 0 0 0 8px rgba(0, 158, 247, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 158, 247, 0); }
}

/* Arrow Containers & Animated Bouncing Arrow */
.tour-arrow-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.arrow-down {
  margin-top: 10px;
}

.arrow-up {
  margin-bottom: 10px;
}

.tour-arrow-bounce {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  animation: tourArrowBounce 1s infinite alternate ease-in-out;
  filter: drop-shadow(0 4px 6px rgba(0, 158, 247, 0.35));
}

@keyframes tourArrowBounce {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(8px);
  }
}

.tour-arrow-pill {
  background: linear-gradient(135deg, #009ef7 0%, #0077c5 100%);
  color: #ffffff;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
}

.hover-scale:hover {
  transform: scale(1.1);
  transition: transform 0.15s ease;
}

/* Dark mode */
[data-bs-theme="dark"] .tour-card {
  background: #1e1e2d !important;
  border-color: #2b2b40 !important;
  color: #f1f1f4 !important;
  box-shadow: 0 20px 40px -8px rgba(0, 0, 0, 0.6), 0 0 25px rgba(0, 158, 247, 0.25) !important;
}

[data-bs-theme="dark"] .tour-card .text-gray-900 {
  color: #ffffff !important;
}

[data-bs-theme="dark"] .tour-card .text-gray-700 {
  color: #c4c5d6 !important;
}

[data-bs-theme="dark"] .tour-card .border-light {
  border-color: #2b2b40 !important;
}

[data-bs-theme="dark"] .tour-card .border-gray-100 {
  border-color: #2b2b40 !important;
}
</style>
